import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ICONS } from "../constants";
import VenmoBadge from "./VenmoBadge";
import ZelleBadge from "./ZelleBadge";
import EditContributor from "../containers/EditContributor";
import ContributorSettlementRow from "./ContributorSettlementRow";
import RemoveContributorPrompt from "../containers/RemoveContributorPrompt";
import { toggleModal, amountDataToDisplay } from "../utils/utils";
import { patchSettlementItem } from "../services/AnimalSplitCore";
import SettlementItemModal from "./SettlementItemModal";
import "./RowHighlight.css";

export default function ContributorRow(props) {
  const {
    groupId,
    groupName,
    contributor,
    renderGroup,
    settlements,
    netTotals,
    contributorIdDict,
    settlementType,
    setIsLoading,
    isSettled = false,
    settlementInteractionBlockedUntil = 0,
  } = props;
  const modalId = `edit-contributor-modal-${contributor.id}`;
  const removeContributorPromptModalId = `remove-contributor-prompt-modal-${contributor.id}`;
  const settlementItemModalId = `settlement-item-modal-${contributor.id}`;
  const [activeSettlement, setActiveSettlement] = useState(null);
  const [activeTarget, setActiveTarget] = useState(null);

  const netCentsRaw = netTotals?.[contributor.id];
  const netCents = Number.isFinite(Number(netCentsRaw)) ? Number(netCentsRaw) : null;
  const showNet = !isSettled && netCents !== null;
  const netBadgeVariant =
    netCents === null
      ? "as-net-badge--zero"
      : netCents > 0
        ? "as-net-badge--pos"
        : netCents < 0
          ? "as-net-badge--neg"
          : "as-net-badge--zero";
  const netText =
    netCents === null
      ? ""
      : `$ ${netCents < 0 ? "- " : ""}${amountDataToDisplay(Math.abs(netCents))}`;

  const showEdit = useCallback(() => {
    // Match TransactionRow behavior: defer so the DOM/portal is definitely mounted.
    window.setTimeout(() => {
      const el = document.getElementById(modalId);
      if (!el || !window?.bootstrap?.Modal) return;
      try {
        // Bootstrap 5.0.x (this app loads 5.0.1 via CDN) doesn't have getOrCreateInstance.
        const modal = window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
        modal.show();
      } catch (e) {
        // Fallback: legacy helper (still Bootstrap-based) in case Modal.show() throws.
        try {
          toggleModal(modalId);
        } catch (_) {
          // ignore
        }
      }
    }, 0);
  }, [modalId]);

  const editContributorModal = (
    <EditContributor
      modalId={modalId}
      groupId={groupId}
      contributor={contributor}
      renderGroup={renderGroup}
      setIsLoading={setIsLoading}
      isSettled={isSettled}
    />
  );

  const removeContributorPromptModal = (
    <RemoveContributorPrompt
      modalId={removeContributorPromptModalId}
      groupId={groupId}
      renderGroup={renderGroup}
      contributor={contributor}
      setIsLoading={setIsLoading}
    />
  );

  const handleTogglePaid = useCallback(
    async (settlementItemId, nextPaid) => {
      if (!groupId || !settlementItemId) return;
      // One-way: only allow marking as paid.
      if (!nextPaid) return;
      setIsLoading(true);
      try {
        const { payload } = await patchSettlementItem(groupId, settlementItemId, {
          paid: Boolean(nextPaid),
        });
        renderGroup(payload);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [groupId, renderGroup, setIsLoading]
  );

  const openSettlementDetail = useCallback(
    (settlement) => {
      // Prevent accidental "tap-through" right after settle.
      if (Date.now() < Number(settlementInteractionBlockedUntil || 0)) return;
      setActiveSettlement(settlement || null);
      const target = contributorIdDict?.[settlement?.target] || null;
      setActiveTarget(target);
    },
    [contributorIdDict, settlementInteractionBlockedUntil]
  );

  const cleanupSettlementModalChrome = useCallback(() => {
    document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");
  }, []);

  // Show modal after it mounts (portal renders only when activeSettlement is set).
  useEffect(() => {
    if (!activeSettlement) return;
    const el = document.getElementById(settlementItemModalId);
    if (!el || !window?.bootstrap?.Modal) return;

    const onHidden = () => {
      setActiveSettlement(null);
      setActiveTarget(null);
      cleanupSettlementModalChrome();
    };

    el.addEventListener("hidden.bs.modal", onHidden);

    try {
      const inst =
        window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
      inst.show();
    } catch (e) {
      // ignore
    }

    return () => el.removeEventListener("hidden.bs.modal", onHidden);
  }, [activeSettlement, settlementItemModalId, cleanupSettlementModalChrome]);

  const closeSettlementDetail = useCallback(() => {
    const el = document.getElementById(settlementItemModalId);
    if (!el || !window?.bootstrap?.Modal) {
      setActiveSettlement(null);
      setActiveTarget(null);
      cleanupSettlementModalChrome();
      return;
    }
    try {
      const inst =
        window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
      inst.hide();
    } catch (e) {
      setActiveSettlement(null);
      setActiveTarget(null);
      cleanupSettlementModalChrome();
    }
    // Clear React state in hidden.bs.modal (see effect above), not here — avoids
    // unmounting the portal mid-animation and leaving a blocking backdrop.
  }, [settlementItemModalId, cleanupSettlementModalChrome]);

  return (
    <>
    <div>
      <div
        className="as-row-surface d-flex align-items-center justify-content-between"
      >
        <div
          className="as-row-click-zone truncate d-flex align-items-center"
          style={{ minWidth: 0, flex: 1 }}
          role="button"
          tabIndex={0}
          onClick={showEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              showEdit();
            }
          }}
          aria-label={isSettled ? "Edit (name locked)" : "Edit"}
          title={isSettled ? "Edit (name locked)" : "Edit"}
        >
          <div className="as-row-leading-badge me-2" aria-hidden="true">
            <span className="fs-4">{ICONS[contributor.icon]}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="as-row-title text-truncate" style={{ maxWidth: "100%" }}>
              {contributor.name}
            </div>
            <div className="as-row-subtitle d-inline-flex align-items-center gap-1 flex-wrap">
              <VenmoBadge username={contributor.venmoUsername} />
              <ZelleBadge
                zelleUsername={contributor.zelleUsername}
                zelleRecipientName={contributor.zelleRecipientName}
              />
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {showNet ? (
            <span
              className={`as-net-badge ${netBadgeVariant}`}
              title="Net total (before settlement)"
              aria-label="Net total (before settlement)"
            >
              {netText}
            </span>
          ) : null}
          <i className="bi bi-chevron-right as-row-chevron" aria-hidden="true" />
        <button
          className={isSettled ? "btn btn-sm" : "btn btn-outline-danger btn-sm"}
          onClick={(e) => {
            e.stopPropagation();
            if (isSettled) return;
            toggleModal(removeContributorPromptModalId);
          }}
          disabled={isSettled}
          aria-label={isSettled ? "Settled" : "Delete"}
          title={isSettled ? "Settled" : "Delete"}
          style={isSettled ? { color: '#dc3545', cursor: 'not-allowed' } : {}}
        >
          <i className={isSettled ? "bi-lock-fill" : "bi-trash-fill"} />
        </button>
        </div>
      </div>
      <div>
        {settlements?.[settlementType]?.[contributor.id]?.map((settlement) => {
          const target = contributorIdDict?.[settlement?.target];
          return (
            <ContributorSettlementRow
              key={settlement?.id || `${settlement?.target}:${settlement?.amount}`}
              name={target?.name}
              icon={target?.icon}
              amount={settlement?.amount}
              isPaid={Boolean(settlement?.paid)}
              onOpen={() => openSettlementDetail(settlement)}
            />
          );
        })}
      </div>
    </div>
    {typeof document !== "undefined" && activeSettlement
      ? createPortal(
          <SettlementItemModal
            modalId={settlementItemModalId}
            groupName={groupName}
            fromContributor={contributor}
            toContributor={activeTarget}
            amountCents={activeSettlement?.amount || 0}
            isPaid={Boolean(activeSettlement?.paid)}
            onClose={closeSettlementDetail}
            onMarkPaid={
              activeSettlement?.id
                ? async () => {
                    await handleTogglePaid(activeSettlement.id, true);
                    closeSettlementDetail();
                  }
                : null
            }
          />,
          document.body
        )
      : null}
    {typeof document !== "undefined" ? createPortal(editContributorModal, document.body) : editContributorModal}
    {typeof document !== "undefined" ? createPortal(removeContributorPromptModal, document.body) : removeContributorPromptModal}
    </>
  );
}
